import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { LogOut, Calendar, Clock, ArrowRight, User, Lock, Shield, HeartPulse, FileText, Package, ChevronDown, ChevronUp, Star, HelpCircle, Gift } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { formatearFecha } from '../i18n/fechas';
import CambioFlow from '../components/client/CambioFlow';
import MonthlyCalendarView from '../components/client/MonthlyCalendarView';
import ClientSectionHeader from '../components/client/ClientSectionHeader';
import '../components/client/cabeceraSeccion.css';
import ConsentimientoGDPRModal from '../components/ConsentimientoGDPRModal';
import FotoPerfilObligatoria from '../components/client/FotoPerfilObligatoria';

// ¿Una clase (fecha 'YYYY-MM-DD' + hora 'HH:MM') YA HA PASADO respecto a AHORA en
// Europe/Madrid? (Gestor de cambios · Ann/Guardián 2026-07-13). Compara reloj de pared
// de Madrid: convierte "ahora" a hora de Madrid y la clase a su hora local de ese día.
export const claseYaPasoMadrid = (fecha, hora) => {
  if (!fecha || !hora) return false;
  try {
    const [h, m] = String(hora).split(':').map(Number);
    if (Number.isNaN(h) || Number.isNaN(m)) return false;
    const ahoraMadrid = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Madrid' }));
    const claseDt = new Date(`${fecha}T00:00:00`);
    claseDt.setHours(h, m, 0, 0);
    return claseDt <= ahoraMadrid;
  } catch (e) {
    return false; // ante la duda, no ocultamos (el backend sigue filtrando)
  }
};

const ClientDashboard = ({ user, onLogout, onUserUpdate, onBackToSelector }) => {
  // i18n zona cliente (interior CA · F2): namespace gestor
  const { t } = useTranslation('gestor');
  const [monthData, setMonthData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCambioFlow, setShowCambioFlow] = useState(false);
  const [showISAviso, setShowISAviso] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedClase, setSelectedClase] = useState(null);
  const [visibilityConfig, setVisibilityConfig] = useState(null);
  const [showConsentimiento, setShowConsentimiento] = useState(false);
  const [consentimientoVerificado, setConsentimientoVerificado] = useState(false);
  const [showAyudaClaseAdicional, setShowAyudaClaseAdicional] = useState(false);

  // Estado para verificación de foto de perfil
  const [fotoEstado, setFotoEstado] = useState(null);
  const [fotoVerificada, setFotoVerificada] = useState(false);
  const [fotoMotivoRechazo, setFotoMotivoRechazo] = useState(null);

  // Estado para mis bonos
  const [misBonos, setMisBonos] = useState([]);
  const [loadingBonos, setLoadingBonos] = useState(true);
  const [bonosExpanded, setBonosExpanded] = useState(false);
  const [canjeBon, setCanjeBon] = useState(null); // bonificación en proceso de canje

  // Verificar estado de la foto de perfil
  useEffect(() => {
    const verificarFoto = async () => {
      try {
        const response = await axios.get(`${API}/cliente/foto-perfil-estado`);
        setFotoEstado(response.data.foto_estado);
        setFotoMotivoRechazo(response.data.foto_motivo_rechazo);
        setFotoVerificada(true);
      } catch (error) {
        console.error('Error verificando foto:', error);
        setFotoVerificada(true);
      }
    };
    verificarFoto();
  }, []);

  // Cargar bonos del cliente
  useEffect(() => {
    const fetchMisBonos = async () => {
      try {
        const response = await axios.get(`${API}/cliente/mis-bonos`);
        setMisBonos(response.data.bonos || []);
      } catch (error) {
        console.error('Error loading mis bonos:', error);
      } finally {
        setLoadingBonos(false);
      }
    };
    fetchMisBonos();
  }, []);

  // Callback cuando el cliente sube una foto
  const handleFotoSubida = async () => {
    // Recargar estado de foto
    try {
      const response = await axios.get(`${API}/cliente/foto-perfil-estado`);
      setFotoEstado(response.data.foto_estado);
      setFotoMotivoRechazo(response.data.foto_motivo_rechazo);
    } catch (error) {
      console.error('Error recargando estado de foto:', error);
    }
  };

  // Verificar si el cliente ha aceptado los términos
  useEffect(() => {
    const verificarConsentimiento = async () => {
      try {
        const response = await axios.get(`${API}/cliente/estado-consentimiento`);
        // Solo mostrar popup si está habilitado Y el cliente no ha aceptado
        if (response.data.popup_habilitado && !response.data.terminos_aceptados) {
          setShowConsentimiento(true);
        }
        setConsentimientoVerificado(true);
      } catch (error) {
        console.error('Error verificando consentimiento:', error);
        setConsentimientoVerificado(true);
      }
    };
    verificarConsentimiento();
  }, []);

  // Cargar aviso IS desde BD
  const [avisoIS, setAvisoIS] = useState(null);
  useEffect(() => {
    if (user?.incidencia_salud_activa) {
      axios.get(`${API}/settings/avisos/aviso_is_cliente`).then(r => setAvisoIS(r.data.contenido)).catch(() => {});
    }
  }, [user?.incidencia_salud_activa]);


  // Mostrar aviso de IS al entrar si el cliente tiene IS activa
  useEffect(() => {
    if (user?.incidencia_salud_activa && consentimientoVerificado && !showConsentimiento) {
      setShowISAviso(true);
    }
  }, [user?.incidencia_salud_activa, consentimientoVerificado, showConsentimiento]);

  // Cargar configuración de visibilidad de documentos legales
  useEffect(() => {
    const fetchVisibility = async () => {
      try {
        const response = await axios.get(`${API}/settings/legal-visibility`);
        setVisibilityConfig(response.data);
      } catch (error) {
        console.error('Error fetching visibility config:', error);
      }
    };
    fetchVisibility();
  }, []);

  useEffect(() => {
    fetchMonthData();
  }, [currentMonth]);

  const fetchMonthData = async (silent = false) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/client/mi-mes`, {
        params: {
          year: currentMonth.getFullYear(),
          month: currentMonth.getMonth() + 1
        }
      });
      setMonthData(response.data);
    } catch (error) {
      if (!silent) {
        toast.error(t('errores.cargarDatos'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClaseClick = (clase) => {
    // BUG gestor de cambios (Ann/Guardián 2026-07-13): no permitir elegir como ORIGEN una
    // clase que YA HA PASADO (hora en Europe/Madrid). Coherente con el filtro del backend.
    if (claseYaPasoMadrid(clase?.fecha, clase?.hora)) {
      toast.error(t('errores.clasePasada'));
      return;
    }
    // Bloquear si tiene deuda
    if (user.tiene_deuda) {
      toast.error(t('errores.bloqueadoDeuda'));
      return;
    }

    // Bloquear si tiene Incidencia de Salud activa y gestor_cambios bloqueado
    if (user.incidencia_salud_activa) {
      const bloqueados = user.accesos_bloqueados_is || [];
      // Bloquear si es modelo legacy (sin array) o si gestor_cambios está en la lista
      if (bloqueados.length === 0 || bloqueados.includes('gestor_cambios')) {
        toast.error(t('errores.bloqueadoIS'));
        setShowISAviso(true);
        return;
      }
    }

    setSelectedClase(clase);
    setShowCambioFlow(true);
  };

  const handleCambioComplete = async () => {
    setShowCambioFlow(false);
    setSelectedClase(null);
    // Reload data silently (don't show error toast if it fails, since cambio was successful)
    await fetchMonthData(true);
    if (onUserUpdate) {
      try {
        await onUserUpdate();
      } catch (error) {
        // Silently fail - the cambio was successful
      }
    }
  };

  const changeMonth = (direction) => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + direction);
    setCurrentMonth(newDate);
  };

  const goToCurrentMonth = () => {
    setCurrentMonth(new Date());
  };

  const getMonthName = () => {
    // "Julio de 2026" — solo la primera letra en mayúscula (el "de" en minúscula).
    // i18n F2: el locale sale del idioma activo (helper fechas.js).
    const s = formatearFecha(currentMonth, { month: 'long', year: 'numeric' });
    return s.charAt(0).toUpperCase() + s.slice(1);
  };

  // NOTA: La verificación de foto obligatoria está DESACTIVADA temporalmente
  // TODO: Reactivar cuando se requiera - ver tareas pendientes
  // El código de bloqueo por foto se mantiene comentado para futuro uso

  /*
  // Mostrar pantalla de carga mientras verificamos
  if (!fotoVerificada) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // BLOQUEO: Si no tiene foto aprobada, mostrar pantalla de foto obligatoria
  // Estado válido: 'aprobada'. Estados que requieren foto: null, 'pendiente', 'rechazada'
  if (fotoEstado !== 'aprobada') {
    // Si está pendiente, mostrar mensaje de espera
    if (fotoEstado === 'pendiente') {
      return (
        <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-lg shadow-xl text-center">
            <CardHeader>
              <div className="mx-auto w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mb-4">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              <CardTitle className="text-2xl text-gray-800">Foto Pendiente de Validación</CardTitle>
              <CardDescription className="text-base">
                Tu fotografía ha sido recibida y está siendo revisada por nuestro equipo.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-amber-800 text-sm">
                  Una vez validada tu foto, podrás acceder a todas las funcionalidades de la plataforma.
                  Este proceso suele ser muy rápido.
                </p>
              </div>
              <Button 
                variant="outline" 
                onClick={onLogout}
                className="w-full"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Cerrar Sesión
              </Button>
            </CardContent>
          </Card>
        </div>
      );
    }
    
    // Si no tiene foto o fue rechazada, mostrar formulario de subida
    return (
      <FotoPerfilObligatoria 
        user={user} 
        onFotoSubida={handleFotoSubida}
        motivoRechazo={fotoMotivoRechazo}
      />
    );
  }
  */

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // BLOQUEO: Si el cliente no ha aceptado el consentimiento, mostrar pantalla de espera con modal obligatorio
  if (showConsentimiento) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50 flex items-center justify-center p-4">
        {/* Fondo con mensaje */}
        <div className="text-center max-w-md">
          <div className="bg-white rounded-2xl shadow-xl p-8 mb-4">
            <FileText className="w-16 h-16 text-teal-600 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-800 mb-2">{t('consentimiento.titulo')}</h2>
            <p className="text-gray-600">
              {t('consentimiento.texto')}
            </p>
          </div>
        </div>

        {/* Modal de Consentimiento GDPR */}
        <ConsentimientoGDPRModal
          isOpen={true}
          onAccept={() => {
            setShowConsentimiento(false);
            if (onUserUpdate) {
              onUserUpdate({ ...user, terminos_aceptados: true });
            }
          }}
          onReject={onLogout}
          userName={user?.nombre || t('comun.cliente')}
        />
      </div>
    );
  }

  // Límite desde la config administrable (max_cambios_mes). Fallback 7 solo si el
  // backend aún no envía el campo (p. ej. versión previa al deploy del fix).
  const maxCambiosMes = monthData?.max_cambios_mes ?? 10; // fallback provisional (config real llega del backend al desplegar)
  const cambiosDisponibles = Math.max(0, maxCambiosMes - (monthData?.cambios_mes || 0));
  const clasesPendientes = Math.max(0, 8 - (monthData?.clases_mes || 0));

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-cyan-50 to-blue-50" data-testid="client-dashboard">
      {/* Cabecera ÚNICA de sección (barra teal fina full-bleed, igual que "Clases Online") */}
      <ClientSectionHeader
        title={t('cabecera.titulo')}
        subtitle={`${user.nombre} ${user.primer_apellido}${user.servicio === 'continuo_8'
          ? ' · ' + t('cabecera.continuo8')
          : (user.servicio === 'fijo' || (user.turno_fijo && user.turno_fijo.length > 0))
            ? ' · ' + t('cabecera.clasesFijas')
            : ''}`}
        onBack={onBackToSelector}
        wrapClass="md:max-w-2xl md:mx-auto md:px-4"
      />

      {/* Banner de Deuda - Permanente y No Cerrable */}
      {user.tiene_deuda && (
        <div className="bg-yellow-400 border-b-4 border-yellow-600 shadow-lg sticky top-0 z-50">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <svg className="w-10 h-10 text-yellow-900 animate-pulse" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-yellow-900 mb-1">
                  {t('deuda.titulo')}
                </h3>
                <p className="text-base font-medium text-yellow-900">
                  {t('deuda.texto')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Banner de Incidencia de Salud - Permanente */}
      {user.incidencia_salud_activa && !user.tiene_deuda && (
        <div className="bg-amber-100 border-b-4 border-amber-400 shadow-lg sticky top-0 z-50" data-testid="is-banner">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0">
                <HeartPulse className="w-10 h-10 text-amber-700" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-bold text-amber-800 mb-1">
                  {t('is.bannerTitulo')}
                </h3>
                <p className="text-sm font-medium text-amber-700">
                  {t('is.bannerTexto')}{' '}
                  <button
                    onClick={() => setShowISAviso(true)}
                    className="underline hover:text-amber-900 font-semibold"
                    data-testid="is-banner-ver-info"
                  >
                    {t('is.verInfo')}
                  </button>
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 pt-2 pb-8 space-y-2.5 gc">
        {/* El mensaje/novedad al cliente (editor de Controles) YA NO va aquí encima
            del gestor de cambios (decisión Ann): va en la MARQUESINA de la página
            inicial (landing). Aquí se retira el banner. */}

        {/* Stats · compactas y discretas (datos presentes, sin destacar) */}
        <div className="gc-stats">
          <div className="gc-stat">
            <div className="gc-srow"><span className="gc-lbl">{t('stats.cambiosMes')}</span></div>
            <div className="gc-big"><b className="gc-num-teal" data-testid="cambios-counter">{monthData?.cambios_mes || 0}</b><span className="gc-of">/ {maxCambiosMes}</span></div>
          </div>
          <div className="gc-stat">
            <div className="gc-srow">
              <span className="gc-lbl">
                {t('stats.clasesAdicionales')}
                <button onClick={() => setShowAyudaClaseAdicional(true)} aria-label={t('stats.ayudaAria')} style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer', lineHeight: 0 }}>
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#B7C6CB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9" /><path d="M9.5 9.5a2.5 2.5 0 0 1 4.5 1.5c0 1.5-2 2-2 3M12 17h.01" /></svg>
                </button>
              </span>
            </div>
            <div className="gc-big"><b className="gc-num-ink">{monthData?.clases_adicionales_mes ?? 0}</b><span className="gc-sub">{t('stats.esteMes')}</span></div>
          </div>
          {/* Tus bonificaciones · nº real de clases regalo disponibles (mi-mes) */}
          <div className="gc-stat">
            <div className="gc-srow"><span className="gc-lbl">{t('stats.bonificaciones')}</span></div>
            <div className="gc-big"><b className="gc-num-ink" data-testid="bonificaciones-counter">{monthData?.bonificaciones_disponibles || 0}</b><span className="gc-sub">{t('stats.disponibles')}</span></div>
          </div>
        </div>

        {/* Continuo 8 · solo si aplica (funcionalidad conservada) */}
        {monthData?.servicio === 'continuo_8' && (
          <div className="gc-stat">
            <div className="gc-srow"><span className="gc-lbl">{t('stats.clasesContinuo8')}</span></div>
            <div className="gc-big"><b className="gc-num-ink">{monthData?.clases_disponibles_continuo8 || 0}</b><span className="gc-sub">{t('stats.disponibles')}</span></div>
          </div>
        )}

        {/* Navegador de mes (maqueta Ann) */}
        <div className="gc-mnav">
          <div>
            <h2>{getMonthName()}</h2>
            <p>{t('mes.sub')}</p>
          </div>
          <div className="gc-ctrls">
            <button className="gc-mbtn sq" data-testid="prev-month-button" onClick={() => changeMonth(-1)} aria-label={t('mes.anterior')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M15 6l-6 6 6 6" /></svg>
            </button>
            <button className="gc-mbtn txt" data-testid="current-month-button" onClick={goToCurrentMonth}>{t('mes.hoy')}</button>
            <button className="gc-mbtn sq" data-testid="next-month-button" onClick={() => changeMonth(1)} aria-label={t('mes.siguiente')}>
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round"><path d="M9 6l6 6-6 6" /></svg>
            </button>
          </div>
        </div>

        {/* Monthly Calendar View */}
        {monthData && (
          <MonthlyCalendarView
            monthData={monthData}
            onRefresh={fetchMonthData}
            onClaseClick={handleClaseClick}
            cambiosDisponibles={cambiosDisponibles}
          />
        )}

        {/* Clase bonificada (regalo) disponible · MISMA tarjeta de clase que las
            demás, pero en azul y con "Clase bonificada" en vez del día. Al
            pincharla se abre el MISMO modal de cambios (CambioFlow) para elegir
            la clase libre. Nada nuevo para el cliente. */}
        {Array.isArray(monthData?.bonificaciones) && monthData.bonificaciones.length > 0 && (
          <div className="tl" data-testid="bonificaciones-cliente">
            <div className="space-y-2">
              {monthData.bonificaciones.map((bon, i) => (
                <button
                  key={bon.credito_id || i}
                  type="button"
                  className="nt-slot bonificada"
                  data-testid="canjear-bonificacion"
                  onClick={() => { setSelectedClase(null); setCanjeBon(bon); }}
                  title={t('bonif.elegirTitle')}
                >
                  <span className="nt-ic"><Gift className="w-5 h-5" /></span>
                  <div className="nt-body">
                    <div className="nt-time">{bon.motivo || t('comun.claseBonificada')}</div>
                    <div className="nt-sub">
                      {bon.disponibles > 1 ? `${t('bonif.clasesRegalo', { n: bon.disponibles })} · ` : `${t('bonif.claseRegalo')} · `}
                      {bon.caduca
                        ? t('bonif.hastaEl', { fecha: formatearFecha(bon.caduca + 'T00:00:00', { day: '2-digit', month: '2-digit', year: 'numeric' }) })
                        : t('bonif.sinCaducidad')}
                    </div>
                  </div>
                  <span className="nt-badge">{t('bonif.badge')}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Canje de la bonificación = MISMO modal que cambiar una clase */}
        {canjeBon && monthData && (
          <CambioFlow
            monthData={monthData}
            bonificacion={canjeBon}
            onClose={() => setCanjeBon(null)}
            onComplete={() => { setCanjeBon(null); fetchMonthData(true); }}
          />
        )}

        {/* Sección Mis Bonos */}
        {!loadingBonos && misBonos.length > 0 && (
          <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50" data-testid="mis-bonos-section">
            <CardHeader className="pb-2">
              <button
                onClick={() => setBonosExpanded(!bonosExpanded)}
                className="flex items-center justify-between w-full"
                data-testid="toggle-bonos-btn"
              >
                <div className="flex items-center gap-3">
                  <Package className="w-6 h-6 text-purple-600" />
                  <div className="text-left">
                    <CardTitle className="text-lg text-purple-800">{t('bonos.titulo')}</CardTitle>
                    <CardDescription className="text-purple-600">
                      {t('bonos.activos', { n: misBonos.filter(b => b.estado === 'activo').length })}
                    </CardDescription>
                  </div>
                </div>
                {bonosExpanded ? (
                  <ChevronUp className="w-5 h-5 text-purple-600" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-purple-600" />
                )}
              </button>
            </CardHeader>

            {bonosExpanded && (
              <CardContent className="pt-2 space-y-4">
                {misBonos.map((bono) => {
                  const sesionesRestantes = bono.sesiones_totales - bono.sesiones_usadas;
                  const progreso = bono.sesiones_totales > 0
                    ? ((bono.sesiones_usadas / bono.sesiones_totales) * 100).toFixed(0)
                    : 0;
                  const estadoColors = {
                    activo: 'bg-green-100 text-green-700 border-green-300',
                    agotado: 'bg-gray-100 text-gray-700 border-gray-300',
                    caducado: 'bg-red-100 text-red-700 border-red-300'
                  };

                  return (
                    <div
                      key={bono.id}
                      className={`p-4 rounded-xl border-2 ${bono.estado === 'activo' ? 'border-purple-300 bg-white' : 'border-gray-200 bg-gray-50 opacity-75'}`}
                      data-testid={`bono-card-${bono.id}`}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h4 className="font-bold text-purple-800 text-lg">{bono.tipo_nombre}</h4>
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-medium ${estadoColors[bono.estado] || 'bg-gray-100'}`}>
                            {bono.estado === 'activo' ? t('bonos.activo') : bono.estado === 'agotado' ? t('bonos.completado') : t('bonos.caducado')}
                          </span>
                        </div>
                        <div className="text-right">
                          <p className="text-2xl font-bold text-purple-700">{sesionesRestantes}</p>
                          <p className="text-xs text-gray-500">{t('bonos.sesionesRestantes')}</p>
                        </div>
                      </div>

                      {/* Barra de progreso */}
                      <div className="mb-3">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>{t('bonos.progreso')}</span>
                          <span>{t('bonos.usadas', { usadas: bono.sesiones_usadas, totales: bono.sesiones_totales })}</span>
                        </div>
                        <div className="w-full bg-gray-200 rounded-full h-3">
                          <div
                            className="bg-gradient-to-r from-purple-500 to-pink-500 h-3 rounded-full transition-all duration-500"
                            style={{ width: `${progreso}%` }}
                          ></div>
                        </div>
                      </div>

                      {/* Próximas citas programadas */}
                      {bono.sesiones && bono.sesiones.filter(s => s.estado === 'programada').length > 0 && (
                        <div className="mt-3 pt-3 border-t border-purple-100">
                          <p className="text-xs font-semibold text-purple-700 mb-2 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {t('bonos.proximasCitas')}
                          </p>
                          <div className="space-y-1">
                            {bono.sesiones
                              .filter(s => s.estado === 'programada')
                              .slice(0, 3)
                              .map((sesion) => (
                                <div
                                  key={sesion.numero_sesion}
                                  className="flex items-center justify-between text-sm bg-purple-50 rounded-lg px-3 py-2"
                                >
                                  <span className="text-purple-700 font-medium">
                                    {t('bonos.sesionNum', { n: sesion.numero_visible ?? sesion.numero_sesion })}
                                  </span>
                                  <span className="text-purple-600">
                                    {formatearFecha(sesion.fecha, {
                                      weekday: 'short',
                                      day: 'numeric',
                                      month: 'short'
                                    })} - {sesion.hora_inicio}
                                  </span>
                                </div>
                              ))}
                          </div>
                        </div>
                      )}

                      {/* Fechas de compra y caducidad */}
                      <div className="mt-3 flex justify-between text-xs text-gray-500">
                        <span>{t('bonos.compra', { fecha: formatearFecha(bono.fecha_compra) })}</span>
                        <span>{t('bonos.caduca', { fecha: formatearFecha(bono.fecha_caducidad) })}</span>
                      </div>
                    </div>
                  );
                })}
              </CardContent>
            )}
          </Card>
        )}
      </main>

      {showCambioFlow && monthData && !user.tiene_deuda && (
        <CambioFlow
          monthData={monthData}
          selectedClase={selectedClase}
          onClose={() => {
            setShowCambioFlow(false);
            setSelectedClase(null);
          }}
          onComplete={handleCambioComplete}
        />
      )}

      {/* Modal de Aviso - Incidencia de Salud (dinámico desde BD) */}
      <Dialog open={showISAviso} onOpenChange={setShowISAviso}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" data-testid="is-aviso-modal">
          <DialogHeader>
            <div className="flex items-center gap-3 mb-2">
              <div className="p-2 bg-amber-100 rounded-full">
                <HeartPulse className="w-6 h-6 text-amber-600" />
              </div>
              <DialogTitle className="text-xl text-amber-800">
                {avisoIS?.titulo_modal || t('is.modalTitulo')}
              </DialogTitle>
            </div>
          </DialogHeader>

          <div className="space-y-4 text-gray-700">
            <p className="text-sm leading-relaxed">
              {avisoIS?.intro || t('is.intro')}
            </p>

            <ul className="space-y-3 text-sm">
              {(avisoIS?.puntos || [
                t('is.p1'),
                t('is.p2'),
                t('is.p3'),
                t('is.p4')
              ]).map((punto, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-amber-500 font-bold">•</span>
                  <span>{punto}</span>
                </li>
              ))}
            </ul>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mt-4">
              <p className="text-sm text-amber-800">
                {avisoIS?.cierre || t('is.cierre')}
              </p>
            </div>
          </div>

          <DialogFooter className="mt-4">
            <Button
              onClick={() => setShowISAviso(false)}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="is-aviso-entendido-btn"
            >
              {t('comun.entendido')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de Consentimiento GDPR */}
      <ConsentimientoGDPRModal
        isOpen={showConsentimiento}
        onAccept={() => {
          setShowConsentimiento(false);
          if (onUserUpdate) {
            onUserUpdate({ ...user, terminos_aceptados: true });
          }
        }}
        onReject={onLogout}
        userName={user?.nombre || t('comun.cliente')}
      />

      {/* Modal de Ayuda Clases Adicionales */}
      <Dialog open={showAyudaClaseAdicional} onOpenChange={setShowAyudaClaseAdicional}>
        <DialogContent className="sm:max-w-md w-[95%] rounded-xl mx-auto p-0 overflow-hidden bg-white max-h-[90vh] flex flex-col border-2 border-blue-200 shadow-xl">
          <DialogHeader className="p-4 sm:p-6 pb-2 sm:pb-4 border-b bg-gradient-to-br from-blue-50 to-indigo-50">
            <DialogTitle className="text-lg sm:text-xl font-bold flex items-center gap-2 text-blue-900">
              <HelpCircle className="w-6 h-6 text-blue-600" />
              {t('ayuda.titulo')}
            </DialogTitle>
          </DialogHeader>

          <div className="p-5 sm:p-6 overflow-y-auto space-y-4 text-sm sm:text-base text-gray-700">
            <p>
              {t('ayuda.p1')}
            </p>
            <p className="font-medium text-amber-800 bg-amber-50 p-4 rounded-lg border border-amber-200 shadow-sm">
              {t('ayuda.p2')}
            </p>
            <p>
              <Trans t={t} i18nKey="ayuda.p3" values={{ precio: monthData?.precio_clase_adicional || 16 }} components={{ 1: <strong className="text-lg text-blue-700 px-1" /> }} />
            </p>
          </div>

          <DialogFooter className="p-4 sm:p-6 border-t bg-gray-50 flex flex-col justify-end">
            <Button
              onClick={() => setShowAyudaClaseAdicional(false)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white shadow-sm h-12 text-base font-medium"
            >
              {t('comun.entendido')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
export default ClientDashboard;
